import axios from 'axios'
import { clearAuthStorage } from '../auth/authStorage';

// Use relative path so requests go through the same origin / nginx proxy.
// This works in every environment (local dev, staging, production) without
// needing a build-time VITE_API_URL for the dashboard's own API calls.
const API_URL = '/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true
})

const shouldLogApiDebug = () => {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('debug_api') !== '0';
  } catch {
    return true;
  }
};

const getPreferredLanguage = () => {
  try {
    const storedLanguage = localStorage.getItem('i18nextLng');
    if (storedLanguage && storedLanguage.trim()) {
      return storedLanguage;
    }
  } catch {
    // Ignore storage access failures and fall back to navigator/default.
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  return 'gr';
};

const buildDebugUrl = (config: { baseURL?: string; url?: string; params?: unknown }) => {
  const base = (config.baseURL || '').replace(/\/$/, '');
  const rawUrl = (config.url || '').toString();
  const urlWithBase = rawUrl.startsWith('http')
    ? rawUrl
    : `${base}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;

  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(urlWithBase, origin);
    if (config.params && typeof config.params === 'object') {
      Object.entries(config.params as Record<string, unknown>).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        parsed.searchParams.set(key, String(value));
      });
    }
    return parsed.pathname + parsed.search;
  } catch {
    return urlWithBase;
  }
};

api.interceptors.request.use(
    (config) => {
      const method = (config.method || "get").toLowerCase();
      if (["post", "put", "patch", "delete"].includes(method)) {
        const csrfToken = document.cookie
          .split(";")
          .map((part) => part.trim())
          .find((part) => part.startsWith("XSRF-TOKEN="))
          ?.slice("XSRF-TOKEN=".length);
        if (csrfToken) {
          config.headers = config.headers ?? {};
          config.headers["X-CSRF-Token"] = csrfToken;
        }
      }
      config.headers = config.headers ?? {};
      config.headers["Accept-Language"] = getPreferredLanguage();

      if (shouldLogApiDebug()) {
        void buildDebugUrl({
          baseURL: config.baseURL,
          url: config.url?.toString(),
          params: config.params
        });
        // console.info('[API REQUEST]', {
        //   method: method.toUpperCase(),
        //   url: requestUrl,
        //   headers: config.headers ?? null,
        //   payload: config.data ?? null,
        // });
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
      if (shouldLogApiDebug()) {
        void buildDebugUrl({
          baseURL: response.config.baseURL,
          url: response.config.url?.toString(),
          params: response.config.params
        });
        // console.info('[API RESPONSE]', {
        //   method,
        //   url: requestUrl,
        //   status: response.status,
        //   headers: response.headers ?? null,
        //   payload: response.data ?? null,
        // });
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (shouldLogApiDebug()) {
        void buildDebugUrl({
          baseURL: originalRequest?.baseURL,
          url: originalRequest?.url?.toString(),
          params: originalRequest?.params
        });
        // console.error('[API ERROR]', {
        //   method,
        //   url: requestUrl,
        //   status: error.response?.status ?? null,
        //   requestHeaders: originalRequest?.headers ?? null,
        //   responseHeaders: error.response?.headers ?? null,
        //   response: error.response?.data ?? null,
        // });
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const requestUrl = (originalRequest?.url || "").toString();
        const isAuthCheck = requestUrl.includes("/users/me");
        const isOnLogin =
          window.location.pathname === "/login" ||
          window.location.pathname.startsWith("/login/");

        // Only a failed session validation should force navigation to the login page.
        // Other 401s can be route-/resource-specific and should be handled by the caller.
        if (!isOnLogin && isAuthCheck) {
          clearAuthStorage();
          window.location.href = isAuthCheck
            ? "/login?session=expired"
            : "/login";
        }

        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
);

export default api;
